
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot, 
  addDoc,
  serverTimestamp,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { Conversation, ChatMessage } from '../types';
import { createMessageNotification } from './notificationService';

/**
 * Generate a stable conversation ID from two user IDs
 * Sorts the IDs to ensure consistency regardless of order
 */
export const getConversationId = (uid1: string, uid2: string): string => {
  if (uid1 === uid2) {
    throw new Error('Cannot create conversation with yourself');
  }
  return [uid1, uid2].sort().join('_');
};

/**
 * Get or create a conversation between two users
 * Creates the conversation if it doesn't exist
 */
export const getOrCreateConversation = async (
  currentUid: string, 
  otherUid: string
): Promise<string> => {
  if (currentUid === otherUid) {
    throw new Error('Cannot create conversation with yourself');
  }

  const conversationId = getConversationId(currentUid, otherUid);
  const conversationRef = doc(db, 'conversations', conversationId);

  try {
    const conversationSnap = await getDoc(conversationRef);
    
    if (conversationSnap.exists()) {
      return conversationId;
    }

    // Create new conversation with all required fields
    const newConversation: Omit<Conversation, 'id'> = {
      type: 'private', // Use 'private' as per requirements (was 'direct')
      participants: [currentUid, otherUid].sort(),
      participantsMap: {
        [currentUid]: true,
        [otherUid]: true
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(), // ✅ Required for inbox sorting
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastSenderId: currentUid,
      unreadCount: {
        [currentUid]: 0,
        [otherUid]: 0
      }
    };

    await setDoc(conversationRef, newConversation);
    return conversationId;
  } catch (error) {
    console.error('[CHAT] Error getting/creating conversation:', error);
    throw error;
  }
};

/**
 * Helper: Extract timestamp value from Firestore timestamp (supports Timestamp objects, numbers, or null)
 */
const getTimestampValue = (timestamp: any): number => {
  if (!timestamp) return 0;
  if (timestamp.toMillis) return timestamp.toMillis(); // Firestore Timestamp
  if (typeof timestamp === 'number') return timestamp;
  if (timestamp.seconds) return timestamp.seconds * 1000; // Legacy format
  return 0;
};

/**
 * Helper: Get the most recent timestamp from a conversation (updatedAt > lastMessageAt > createdAt)
 */
const getConversationTimestamp = (conv: Conversation): number => {
  const updatedAt = getTimestampValue(conv.updatedAt);
  const lastMessageAt = getTimestampValue(conv.lastMessageAt);
  const createdAt = getTimestampValue(conv.createdAt);
  
  // Priority: updatedAt > lastMessageAt > createdAt
  return updatedAt || lastMessageAt || createdAt;
};

/**
 * Subscribe to conversations list for a user
 * Returns unsubscribe function
 * 
 * NOTE: Client-side sorting to handle legacy conversations without updatedAt/lastMessageAt
 */
export const subscribeToConversations = (
  currentUid: string,
  callback: (conversations: Conversation[]) => void
): (() => void) => {
  // 🔒 CRITICAL FIX: Verify currentUid === auth.currentUser.uid BEFORE creating listener
  const authUid = auth.currentUser?.uid;
  if (!authUid || authUid !== currentUid) {
    // ❌ NOT your conversations → no snapshot, return empty
    if (import.meta.env.DEV) {
      console.warn('[CHAT] subscribeToConversations blocked: currentUid !== auth.currentUser.uid');
    }
    try {
      callback([]);
    } catch {
      // Silently ignore callback errors
    }
    return () => {};
  }

  const conversationsRef = collection(db, 'conversations');
  
  // Query ONLY filters by participation - NO orderBy (client-side sorting instead)
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', currentUid)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      // Map documents to Conversation objects
      const conversations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Conversation));
      
      // Client-side sorting: most recent first
      // Priority: updatedAt > lastMessageAt > createdAt
      conversations.sort((a, b) => {
        const timestampA = getConversationTimestamp(a);
        const timestampB = getConversationTimestamp(b);
        return timestampB - timestampA; // Descending (newest first)
      });
      
      callback(conversations);
    },
    (error) => {
      // CRITICAL: Handle permission-denied errors gracefully - don't crash the UI
      if (error.code === 'permission-denied') {
        if (import.meta.env.DEV) {
          console.warn('[CHAT] Permission denied for conversations query - user may not have access');
        }
        callback([]); // Return empty array - UI shows empty list instead of crashing
      } else {
        if (import.meta.env.DEV) {
          console.error('[CHAT] Error subscribing to conversations:', error);
        }
        callback([]); // Return empty array on any error
      }
    }
  );
};

/**
 * Subscribe to messages in a conversation
 * Returns unsubscribe function
 * 🔒 CRITICAL: Must verify user is authenticated BEFORE creating listener
 */
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: ChatMessage[]) => void
): (() => void) => {
  // 🔒 CRITICAL FIX: Verify user is authenticated BEFORE creating listener
  const authUid = auth.currentUser?.uid;
  if (!authUid) {
    // ❌ Not authenticated → no snapshot
    if (import.meta.env.DEV) {
      console.warn('[CHAT] subscribeToMessages blocked: no authenticated user');
    }
    try {
      callback([]);
    } catch {
      // Silently ignore
    }
    return () => {};
  }

  // NOTE: Firestore rules should verify user is participant in conversation
  // We can't verify participation here without an async getDoc first
  // which would defeat the purpose of real-time updates
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  // Limit initial messages for performance (pagination-ready)
  // TODO: Implement pagination with startAfter for loading older messages
  const MESSAGE_LIMIT = 30;
  const q = query(messagesRef, orderBy('createdAt', 'asc'), limit(MESSAGE_LIMIT));

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ChatMessage));
      callback(messages);
    },
    (error) => {
      // CRITICAL: Handle permission-denied errors gracefully - don't crash the UI
      if (error.code === 'permission-denied') {
        if (import.meta.env.DEV) {
          console.warn('[CHAT] Permission denied for messages query - user may not have access to this conversation');
        }
        callback([]); // Return empty array - UI shows empty messages instead of crashing
      } else {
        if (import.meta.env.DEV) {
          console.error('[CHAT] Error subscribing to messages:', error);
        }
        callback([]); // Return empty array on any error
      }
    }
  );
};

/**
 * Send a message in a conversation
 * Atomically updates conversation metadata and creates message
 */
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  text: string
): Promise<void> => {
  if (!text.trim()) {
    throw new Error('Message text cannot be empty');
  }

  const conversationRef = doc(db, 'conversations', conversationId);
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');

  try {
    // Use transaction to ensure atomicity
    await runTransaction(db, async (transaction) => {
      // Get conversation to find the other participant
      const conversationSnap = await transaction.get(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data() as Conversation;
      
      // Verify sender is a participant
      if (!conversationData.participants.includes(senderId)) {
        throw new Error('Sender is not a participant in this conversation');
      }

      // Create message document
      const messageData: Omit<ChatMessage, 'id'> = {
        senderId,
        text: text.trim(),
        createdAt: serverTimestamp(),
        readBy: {
          [senderId]: true // Sender has read their own message
        },
        type: 'text'
      };

      // Add message (transaction will handle this)
      const messageRef = doc(messagesRef);
      transaction.set(messageRef, messageData);

      // Update conversation metadata
      const truncatedMessage = text.trim().length > 100 
        ? text.trim().substring(0, 100) + '...' 
        : text.trim();

      // ✅ Update unread counts: increment for all participants except sender, set sender to 0
      // Handle legacy conversations where unreadCount might not exist
      const currentUnreadCount = conversationData.unreadCount || {};
      const updatedUnreadCount = { ...currentUnreadCount };
      
      // Initialize unreadCount for all participants if missing (legacy support)
      conversationData.participants.forEach(uid => {
        if (!(uid in updatedUnreadCount)) {
          updatedUnreadCount[uid] = 0;
        }
      });
      
      // Increment unread count for all participants except sender
      conversationData.participants.forEach(uid => {
        if (uid !== senderId) {
          updatedUnreadCount[uid] = (updatedUnreadCount[uid] || 0) + 1;
        } else {
          // ✅ Sender always has 0 unread messages
          updatedUnreadCount[uid] = 0;
        }
      });

      // ✅ Update conversation metadata with all required fields
      transaction.update(conversationRef, {
        lastMessage: truncatedMessage,
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp(), // ✅ Required for inbox sorting
        lastSenderId: senderId,
        unreadCount: updatedUnreadCount
      });
    });

    // ✅ Create in-app notifications for all participants except sender
    // This happens AFTER the transaction to avoid blocking message sending
    const conversationSnap = await getDoc(conversationRef);
    if (conversationSnap.exists()) {
      const conversationData = conversationSnap.data() as Conversation;
      const notificationPromises: Promise<void>[] = [];
      
      // Create notifications for all participants except sender
      conversationData.participants.forEach(participantId => {
        if (participantId !== senderId) {
          notificationPromises.push(
            createMessageNotification(
              participantId,
              senderId,
              conversationId,
              text.trim()
            )
          );
        }
      });

      // Fire and forget - don't block message sending if notifications fail
      Promise.all(notificationPromises).catch(error => {
        console.error('[CHAT] ⚠️ Some notifications failed to create (non-blocking):', error);
      });
    }
  } catch (error) {
    console.error('[CHAT] Error sending message:', error);
    throw error;
  }
};

/**
 * Mark a conversation as read for the current user
 * Sets unreadCount to 0 for the user and marks related notifications as read
 */
export const markConversationRead = async (
  conversationId: string,
  currentUid: string
): Promise<void> => {
  const conversationRef = doc(db, 'conversations', conversationId);

  try {
    const conversationSnap = await getDoc(conversationRef);
    
    if (!conversationSnap.exists()) {
      throw new Error('Conversation not found');
    }

    const conversationData = conversationSnap.data() as Conversation;
    
    // Only update if user is a participant
    if (!conversationData.participants.includes(currentUid)) {
      throw new Error('User is not a participant in this conversation');
    }

    // ✅ Reset unread count to 0 for current user only
    // Handle legacy conversations where unreadCount might not exist
    const currentUnreadCount = conversationData.unreadCount || {};
    const updatedUnreadCount = { ...currentUnreadCount };
    updatedUnreadCount[currentUid] = 0;

    // Update only unreadCount field (atomic update)
    await updateDoc(conversationRef, {
      unreadCount: updatedUnreadCount
    });

    // ✅ Mark all notifications for this conversation as read
    const { markConversationNotificationsAsRead } = await import('./notificationService');
    markConversationNotificationsAsRead(currentUid, conversationId).catch(error => {
      console.error('[CHAT] ⚠️ Failed to mark notifications as read (non-blocking):', error);
    });
  } catch (error) {
    console.error('[CHAT] Error marking conversation as read:', error);
    throw error;
  }
};

/**
 * Create a new group conversation
 */
export const createGroupConversation = async (
  creatorUid: string,
  title: string,
  memberUids: string[],
  description?: string,
  photoURL?: string
): Promise<string> => {
  if (!title.trim()) {
    throw new Error('Group name is required');
  }

  // Ensure creator is included and all participants are unique
  const allParticipants = Array.from(new Set([creatorUid, ...memberUids]));
  
  if (allParticipants.length < 2) {
    throw new Error('Group must have at least 2 members');
  }

  try {
    const conversationsRef = collection(db, 'conversations');
    const newConversation: Omit<Conversation, 'id'> = {
      type: 'group',
      participants: allParticipants,
      participantsMap: allParticipants.reduce((acc, uid) => {
        acc[uid] = true;
        return acc;
      }, {} as { [uid: string]: true }),
      title: title.trim(),
      description: description?.trim(),
      photoURL: photoURL?.trim(),
      createdBy: creatorUid,
      adminsMap: {
        [creatorUid]: true
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(), // ✅ Required for inbox sorting
      lastMessage: '',
      lastMessageAt: serverTimestamp(),
      lastSenderId: creatorUid,
      unreadCount: allParticipants.reduce((acc, uid) => {
        acc[uid] = 0;
        return acc;
      }, {} as { [uid: string]: number })
    };

    const docRef = await addDoc(conversationsRef, newConversation);
    return docRef.id;
  } catch (error) {
    console.error('[CHAT] Error creating group conversation:', error);
    throw error;
  }
};

/**
 * Add members to a group conversation (admin only)
 */
export const addGroupMembers = async (
  conversationId: string,
  adminUid: string,
  newMemberUids: string[]
): Promise<void> => {
  if (newMemberUids.length === 0) {
    throw new Error('No members to add');
  }

  const conversationRef = doc(db, 'conversations', conversationId);

  try {
    await runTransaction(db, async (transaction) => {
      const conversationSnap = await transaction.get(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data() as Conversation;
      
      // Verify it's a group
      if (conversationData.type !== 'group') {
        throw new Error('Not a group conversation');
      }

      // Verify admin is an admin
      if (!conversationData.adminsMap?.[adminUid]) {
        throw new Error('Only admins can add members');
      }

      // Get current participants and add new ones (avoid duplicates)
      const currentParticipants = conversationData.participants || [];
      const updatedParticipants = Array.from(new Set([...currentParticipants, ...newMemberUids]));
      
      // Update participantsMap
      const updatedParticipantsMap = { ...conversationData.participantsMap };
      newMemberUids.forEach(uid => {
        updatedParticipantsMap[uid] = true;
      });

      // Initialize unreadCount for new members
      const updatedUnreadCount = { ...conversationData.unreadCount };
      newMemberUids.forEach(uid => {
        if (!(uid in updatedUnreadCount)) {
          updatedUnreadCount[uid] = 0;
        }
      });

      transaction.update(conversationRef, {
        participants: updatedParticipants,
        participantsMap: updatedParticipantsMap,
        unreadCount: updatedUnreadCount
      });
    });
  } catch (error) {
    console.error('[CHAT] Error adding group members:', error);
    throw error;
  }
};

/**
 * Remove a member from a group conversation (admin only)
 */
export const removeGroupMember = async (
  conversationId: string,
  adminUid: string,
  memberUid: string
): Promise<void> => {
  const conversationRef = doc(db, 'conversations', conversationId);

  try {
    await runTransaction(db, async (transaction) => {
      const conversationSnap = await transaction.get(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data() as Conversation;
      
      // Verify it's a group
      if (conversationData.type !== 'group') {
        throw new Error('Not a group conversation');
      }

      // Verify admin is an admin
      if (!conversationData.adminsMap?.[adminUid]) {
        throw new Error('Only admins can remove members');
      }

      // Cannot remove yourself as admin (use leaveGroup instead)
      if (memberUid === adminUid && conversationData.adminsMap[adminUid]) {
        throw new Error('Use leaveGroup to leave the group');
      }

      // Remove from participants
      const updatedParticipants = conversationData.participants.filter(uid => uid !== memberUid);
      
      if (updatedParticipants.length < 2) {
        throw new Error('Group must have at least 2 members');
      }

      // Update participantsMap
      const updatedParticipantsMap = { ...conversationData.participantsMap };
      delete updatedParticipantsMap[memberUid];

      // Remove from unreadCount
      const updatedUnreadCount = { ...conversationData.unreadCount };
      delete updatedUnreadCount[memberUid];

      // Remove from adminsMap if they were an admin
      const updatedAdminsMap = { ...conversationData.adminsMap };
      delete updatedAdminsMap[memberUid];

      transaction.update(conversationRef, {
        participants: updatedParticipants,
        participantsMap: updatedParticipantsMap,
        unreadCount: updatedUnreadCount,
        adminsMap: updatedAdminsMap
      });
    });
  } catch (error) {
    console.error('[CHAT] Error removing group member:', error);
    throw error;
  }
};

/**
 * Leave a group conversation
 * If user is the last admin, promotes the oldest other participant to admin
 */
export const leaveGroup = async (
  conversationId: string,
  uid: string
): Promise<void> => {
  const conversationRef = doc(db, 'conversations', conversationId);

  try {
    await runTransaction(db, async (transaction) => {
      const conversationSnap = await transaction.get(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error('Conversation not found');
      }

      const conversationData = conversationSnap.data() as Conversation;
      
      // Verify it's a group
      if (conversationData.type !== 'group') {
        throw new Error('Not a group conversation');
      }

      // Verify user is a participant
      if (!conversationData.participants.includes(uid)) {
        throw new Error('User is not a participant');
      }

      // Remove from participants
      const updatedParticipants = conversationData.participants.filter(p => p !== uid);
      
      if (updatedParticipants.length < 2) {
        throw new Error('Group must have at least 2 members');
      }

      // Update participantsMap
      const updatedParticipantsMap = { ...conversationData.participantsMap };
      delete updatedParticipantsMap[uid];

      // Remove from unreadCount
      const updatedUnreadCount = { ...conversationData.unreadCount };
      delete updatedUnreadCount[uid];

      // Handle admin promotion if needed
      let updatedAdminsMap = { ...conversationData.adminsMap };
      const isAdmin = updatedAdminsMap?.[uid];
      
      if (isAdmin) {
        delete updatedAdminsMap[uid];
        
        // Check if there are any remaining admins
        const remainingAdmins = Object.keys(updatedAdminsMap || {});
        if (remainingAdmins.length === 0) {
          // Promote the oldest other participant to admin
          const oldestParticipant = updatedParticipants[0];
          updatedAdminsMap = { [oldestParticipant]: true };
        }
      }

      transaction.update(conversationRef, {
        participants: updatedParticipants,
        participantsMap: updatedParticipantsMap,
        unreadCount: updatedUnreadCount,
        adminsMap: updatedAdminsMap
      });
    });
  } catch (error) {
    console.error('[CHAT] Error leaving group:', error);
    throw error;
  }
};

